import { imageDimensions } from "./image-dimensions";
export const MAX_REPORT_SCREENSHOTS = 3;
export const MAX_SCREENSHOT_BYTES = 200 * 1024;
export const MAX_SCREENSHOT_EDGE = 1920;
export type ReportScreenshot = {
  dataUrl: string;
  width: number;
  height: number;
};
export function screenshotBytes(image: ReportScreenshot): Uint8Array {
  const prefix = "data:image/jpeg;base64,";
  if (
    typeof image.dataUrl !== "string" ||
    !image.dataUrl.startsWith(prefix) ||
    image.dataUrl.length >
      prefix.length + Math.ceil(MAX_SCREENSHOT_BYTES / 3) * 4
  )
    throw new Error("Screenshot is too large or not a JPEG.");
  const encoded = image.dataUrl.slice(prefix.length);
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(encoded))
    throw new Error("Invalid screenshot encoding.");
  const raw = atob(encoded);
  if (
    btoa(raw) !== encoded ||
    raw.length > MAX_SCREENSHOT_BYTES ||
    raw.charCodeAt(0) !== 255 ||
    raw.charCodeAt(1) !== 216 ||
    raw.charCodeAt(raw.length - 2) !== 255 ||
    raw.charCodeAt(raw.length - 1) !== 217
  )
    throw new Error("Invalid screenshot image.");
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}
export function parseScreenshots(value: unknown): ReportScreenshot[] {
  if (!Array.isArray(value) || value.length > MAX_REPORT_SCREENSHOTS)
    throw new Error("Attach up to three screenshots.");
  return value.map((v: unknown) => {
    if (!v || typeof v !== "object") throw new Error("Invalid screenshot.");
    const image = v as ReportScreenshot;
    if (
      !Number.isInteger(image.width) ||
      !Number.isInteger(image.height) ||
      image.width < 1 ||
      image.height < 1 ||
      Math.max(image.width, image.height) > MAX_SCREENSHOT_EDGE
    )
      throw new Error("Invalid screenshot dimensions.");
    screenshotBytes(image);
    return { dataUrl: image.dataUrl, width: image.width, height: image.height };
  });
}
export async function validateScreenshots(images: ReportScreenshot[] = []) {
  for (const image of images) {
    const bytes = screenshotBytes(image);
    const size = await imageDimensions(
      new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "image/jpeg" }),
    );
    if (size.w !== image.width || size.h !== image.height)
      throw new Error("Screenshot dimensions do not match.");
  }
}
export type Crop = { x: number; y: number; width: number; height: number };
export function adjustCrop(crop: Crop, key: keyof Crop, percent: number): Crop {
  if (!Number.isFinite(percent)) return crop;
  const next = { ...crop, [key]: Math.min(1, Math.max(0, percent / 100)) };
  next.x = Math.min(0.99, next.x);
  next.y = Math.min(0.99, next.y);
  next.width = Math.min(1 - next.x, Math.max(0.01, next.width));
  next.height = Math.min(1 - next.y, Math.max(0.01, next.height));
  return next;
}
export function cropFromPoints(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): Crop {
  const clamp = (n: number) => Math.min(1, Math.max(0, n));
  const x1 = clamp(ax),
    y1 = clamp(ay),
    x2 = clamp(bx),
    y2 = clamp(by);
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}
export function pixelCrop(crop: Crop, width: number, height: number) {
  const x = Math.min(width - 1, Math.max(0, Math.round(crop.x * width))),
    y = Math.min(height - 1, Math.max(0, Math.round(crop.y * height)));
  return {
    x,
    y,
    width: Math.max(1, Math.min(width - x, Math.round(crop.width * width))),
    height: Math.max(1, Math.min(height - y, Math.round(crop.height * height))),
  };
}
